import { normalizeKey } from '../../shared/verteoffice/exports/helpers/helpers'
import type { DomainConfigItem } from './domainConfig'
import type { DomainDataList } from './dependencyConfig'

export const WORKLOG_PRORRATEO_COMPANY_ID = 'PRORRATEO'
export const WORKLOG_PRORRATEO_COMPANY_NAME = 'PRORRATEO'
export const WORKLOG_FORCED_PRODUCT = 'N/A'
export const WORKLOG_VERTEBRA_COMPANY_ID = 'cqzqzv50bir5dtgz0pi33oy'
export const WORKLOG_VERTEBRA_COMPANY_NAME = 'VERTEBRA'

export interface WorklogForcedDefaults {
  company_id: string
  company_name: string
  product: string
}

export type WorklogForcedDefaultsLookup = Record<string, WorklogForcedDefaults>

export interface WorklogForcedDefaultsResolution extends WorklogForcedDefaults {
  reason: 'activity' | 'prorrateo'
}

const DEFAULT_ACTIVITY_FORCED_DEFAULTS: WorklogForcedDefaults = {
  company_id: WORKLOG_VERTEBRA_COMPANY_ID,
  company_name: WORKLOG_VERTEBRA_COMPANY_NAME,
  product: WORKLOG_FORCED_PRODUCT,
}

const FALLBACK_INTERNAL_ACTIVITY_NAMES = [
  'BREAK',
  'REGISTRO DE ACTIVIDADES',
  'COPASST',
]

const getExtrasDefaults = (extras: any): WorklogForcedDefaults | null => {
  const defaults = extras?.worklog_defaults
  if (!defaults || typeof defaults !== 'object') return null

  return {
    company_id: String(defaults.company_id ?? WORKLOG_VERTEBRA_COMPANY_ID),
    company_name: String(defaults.company_name ?? WORKLOG_VERTEBRA_COMPANY_NAME),
    product: String(defaults.product ?? WORKLOG_FORCED_PRODUCT),
  }
}

const putDefault = (
  lookup: WorklogForcedDefaultsLookup,
  name: string | null | undefined,
  defaults: WorklogForcedDefaults,
  overwrite = true
) => {
  const normalizedName = normalizeKey(String(name ?? ''))
  if (!normalizedName) return
  if (!overwrite && lookup[normalizedName]) return
  lookup[normalizedName] = defaults
}

export function buildWorklogForcedDefaultsLookup(
  domainsData: Record<string, Array<{ name: string; extras?: any }>> | undefined,
  config: DomainConfigItem[],
  domainDataList: DomainDataList = {}
): WorklogForcedDefaultsLookup {
  const lookup: WorklogForcedDefaultsLookup = {}

  if (domainsData) {
    for (const item of config) {
      if (
        item.type !== 'GENERAL_ACTIVITY' &&
        !item.type.startsWith('ACTIVITY_OBJECTIVE_') &&
        !item.type.startsWith('SPECIFIC_ACTIVITY_')
      ) {
        continue
      }

      const rows = domainsData[item.alias] ?? []
      for (const row of rows) {
        const defaults = getExtrasDefaults(row.extras)
        if (defaults) putDefault(lookup, row.name, defaults)
      }
    }
  }

  for (const activityName of domainDataList.specificActivityAbsentWorkList ?? []) {
    putDefault(lookup, activityName, DEFAULT_ACTIVITY_FORCED_DEFAULTS, false)
  }

  for (const activityName of FALLBACK_INTERNAL_ACTIVITY_NAMES) {
    putDefault(lookup, activityName, DEFAULT_ACTIVITY_FORCED_DEFAULTS, false)
  }

  return lookup
}

export function resolveWorklogForcedDefaults(
  record: Record<string, any>,
  lookup: WorklogForcedDefaultsLookup
): WorklogForcedDefaultsResolution | null {
  const activityValues = [
    record.activity_objective,
    record.specific_activity,
    record.general_activity,
  ]

  for (const value of activityValues) {
    const defaults = lookup[normalizeKey(String(value ?? ''))]
    if (defaults) {
      return { ...defaults, reason: 'activity' }
    }
  }

  const companyId = normalizeKey(String(record.company_id ?? ''))
  const companyName = normalizeKey(String(record.company_name ?? ''))
  if (
    companyId === normalizeKey(WORKLOG_PRORRATEO_COMPANY_ID) ||
    companyName === normalizeKey(WORKLOG_PRORRATEO_COMPANY_NAME)
  ) {
    return {
      company_id: WORKLOG_PRORRATEO_COMPANY_ID,
      company_name: WORKLOG_PRORRATEO_COMPANY_NAME,
      product: WORKLOG_FORCED_PRODUCT,
      reason: 'prorrateo',
    }
  }

  return null
}

export function applyWorklogForcedDefaults<T extends Record<string, any>>(
  record: T,
  lookup: WorklogForcedDefaultsLookup
): T {
  const defaults = resolveWorklogForcedDefaults(record, lookup)
  if (!defaults) return record

  return {
    ...record,
    company_id: defaults.company_id,
    company_name: defaults.company_name,
    product: defaults.product,
  }
}
